import { Injectable, effect, inject } from '@angular/core';
import {
  Firestore,
  collection,
  doc,
  docData,
  getDoc,
  getDocs,
  query,
  where,
} from '@angular/fire/firestore';
import {
  Observable,
  Subject,
  finalize,
  map,
  of,
  shareReplay,
  takeUntil,
} from 'rxjs';
import { AuthService } from '../../core/auth/auth.service';
import {
  WIRE_PROGRAM_ID,
  WIRE_PROGRAMS,
  programFromStored,
  type FitnessPlanProgram,
  type StoredProgramRowWire,
} from './fitness-plan.domain';

export type ProgramGenJobStatus = 'queued' | 'running' | 'done' | 'error';

export interface ProgramGenJob {
  planId: string;
  programId: string;
  status: ProgramGenJobStatus;
  step?: string;
  stepLabel?: string;
  workoutIndex?: number;
  workoutTotal?: number;
  workoutName?: string;
  workoutSubStep?: string;
  retryAttempt?: number;
  retryTotal?: number;
  innerRetryAttempt?: number;
  innerRetryTotal?: number;
  error?: string;
  createdAt?: string;
  updatedAt?: string;
  result?: {
    workouts?: unknown[];
    split?: Record<string, string | null>;
  };
}

export interface ProgramGenJobRecord extends ProgramGenJob {
  jobId: string;
}

@Injectable({ providedIn: 'root' })
export class PlanProgramGenJobService {
  private readonly firestore = inject(Firestore);
  private readonly auth = inject(AuthService);

  private readonly streams = new Map<
    string,
    { stream: Observable<ProgramGenJob | undefined>; stop$: Subject<void> }
  >();

  private readonly planProgramStreams = new Map<
    string,
    {
      stream: Observable<FitnessPlanProgram | undefined>;
      stop$: Subject<void>;
    }
  >();

  /** Latest terminal job per program — survives stale plan refetches. */
  private readonly notedTerminalJobs = new Map<string, ProgramGenJobRecord>();

  private terminalKey(planId: string, programId: string): string {
    return `${planId.trim()}:${programId.trim()}`;
  }

  noteTerminalJob(job: ProgramGenJobRecord): void {
    const planId = job.planId?.trim();
    const programId = job.programId?.trim();
    if (
      !planId ||
      !programId ||
      (job.status !== 'done' && job.status !== 'error')
    ) {
      return;
    }
    this.notedTerminalJobs.set(this.terminalKey(planId, programId), job);
  }

  private static jobUpdatedAt(job: ProgramGenJob): string {
    return typeof job.updatedAt === 'string' ? job.updatedAt : '';
  }

  /** Latest done/error job per program for a plan (Firestore + in-session notes). */
  async listTerminalJobsForPlan(
    uid: string,
    planId: string,
  ): Promise<Map<string, ProgramGenJobRecord>> {
    const owner = uid.trim();
    const pid = planId.trim();
    const byProgram = new Map<string, ProgramGenJobRecord>();
    if (!owner || !pid) {
      return byProgram;
    }

    for (const [key, job] of this.notedTerminalJobs) {
      if (!key.startsWith(`${pid}:`) || job.planId?.trim() !== pid) {
        continue;
      }
      const programId = job.programId?.trim();
      if (!programId) {
        continue;
      }
      byProgram.set(programId, job);
    }

    const colRef = collection(this.firestore, 'users', owner, 'programGenJobs');
    const q = query(colRef, where('planId', '==', pid));
    const snap = await getDocs(q);
    for (const docSnap of snap.docs) {
      const data = docSnap.data() as ProgramGenJob;
      if (data.status !== 'done' && data.status !== 'error') {
        continue;
      }
      const programId = data.programId?.trim();
      if (!programId) {
        continue;
      }
      const record: ProgramGenJobRecord = { ...data, jobId: docSnap.id };
      const existing = byProgram.get(programId);
      if (
        !existing ||
        PlanProgramGenJobService.jobUpdatedAt(record) >=
          PlanProgramGenJobService.jobUpdatedAt(existing)
      ) {
        byProgram.set(programId, record);
      }
    }
    return byProgram;
  }

  constructor() {
    let prevUid: string | undefined;
    effect(() => {
      const uid = this.auth.user()?.uid?.trim() || undefined;
      if (prevUid !== undefined && prevUid !== uid) {
        this.stopAll();
      }
      prevUid = uid;
    });
  }

  watchJob(uid: string, jobId: string): Observable<ProgramGenJob | undefined> {
    const key = jobId.trim();
    const owner = uid.trim();
    if (!key || !owner) {
      return new Observable((sub) => {
        sub.next(undefined);
        sub.complete();
      });
    }

    const existing = this.streams.get(key);
    if (existing) {
      return existing.stream;
    }

    const stop$ = new Subject<void>();
    const ref = doc(this.firestore, 'users', owner, 'programGenJobs', key);
    const stream = docData(ref).pipe(
      map((data) => (data ? (data as ProgramGenJob) : undefined)),
      takeUntil(stop$),
      finalize(() => {
        this.streams.delete(key);
      }),
      shareReplay({ bufferSize: 1, refCount: true }),
    );

    this.streams.set(key, { stream, stop$ });
    return stream;
  }

  stopWatching(jobId: string): void {
    const key = jobId.trim();
    const entry = this.streams.get(key);
    if (!entry) {
      return;
    }
    entry.stop$.next();
    entry.stop$.complete();
    this.streams.delete(key);
  }

  stopAll(): void {
    for (const key of [...this.streams.keys()]) {
      this.stopWatching(key);
    }
    for (const key of [...this.planProgramStreams.keys()]) {
      this.stopWatchingPlanProgram(key);
    }
  }

  /**
   * Live program row from Firestore (generation + artifacts). One listener per plan/program.
   */
  watchPlanProgram(
    uid: string,
    planId: string,
    programId: string,
  ): Observable<FitnessPlanProgram | undefined> {
    const owner = uid.trim();
    const pid = planId.trim();
    const progId = programId.trim();
    const key = `${owner}:${pid}:${progId}`;
    if (!owner || !pid || !progId) {
      return of(undefined);
    }

    const existing = this.planProgramStreams.get(key);
    if (existing) {
      return existing.stream;
    }

    const stop$ = new Subject<void>();
    const ref = doc(this.firestore, 'users', owner, 'fitnessPlans', pid);
    const stream = docData(ref).pipe(
      map((data) => {
        if (!data) {
          return undefined;
        }
        const rawPrograms = data[WIRE_PROGRAMS];
        if (!Array.isArray(rawPrograms)) {
          return undefined;
        }
        for (const row of rawPrograms) {
          if (!row || typeof row !== 'object') {
            continue;
          }
          const wire = row as StoredProgramRowWire;
          const bid = wire[WIRE_PROGRAM_ID];
          if (typeof bid === 'string' && bid.trim() === progId) {
            return programFromStored(wire);
          }
        }
        return undefined;
      }),
      takeUntil(stop$),
      finalize(() => {
        this.planProgramStreams.delete(key);
      }),
      shareReplay({ bufferSize: 1, refCount: true }),
    );

    this.planProgramStreams.set(key, { stream, stop$ });
    return stream;
  }

  stopWatchingPlanProgram(streamKey: string): void {
    const entry = this.planProgramStreams.get(streamKey);
    if (!entry) {
      return;
    }
    entry.stop$.next();
    entry.stop$.complete();
    this.planProgramStreams.delete(streamKey);
  }

  planProgramStreamKey(
    uid: string,
    planId: string,
    programId: string,
  ): string {
    return `${uid.trim()}:${planId.trim()}:${programId.trim()}`;
  }

  /** Queued or running jobs for a plan (used after refetch when program rows may be stale). */
  async listActiveJobsForPlan(
    uid: string,
    planId: string,
  ): Promise<ProgramGenJobRecord[]> {
    const owner = uid.trim();
    const pid = planId.trim();
    if (!owner || !pid) {
      return [];
    }
    const colRef = collection(this.firestore, 'users', owner, 'programGenJobs');
    const q = query(colRef, where('planId', '==', pid));
    const snap = await getDocs(q);
    const out: ProgramGenJobRecord[] = [];
    for (const docSnap of snap.docs) {
      const data = docSnap.data() as ProgramGenJob;
      if (data.status !== 'queued' && data.status !== 'running') {
        continue;
      }
      out.push({ ...data, jobId: docSnap.id });
    }
    return out;
  }

  async getJob(
    uid: string,
    jobId: string,
  ): Promise<ProgramGenJobRecord | null> {
    const owner = uid.trim();
    const key = jobId.trim();
    if (!owner || !key) {
      return null;
    }
    const ref = doc(this.firestore, 'users', owner, 'programGenJobs', key);
    const snap = await getDoc(ref);
    if (!snap.exists()) {
      return null;
    }
    return { ...(snap.data() as ProgramGenJob), jobId: snap.id };
  }
}
